import { notFound, redirect } from "@tanstack/react-router";

import { buildHref } from "@softmax/web-addresses";
import {
  isPolicyUuid,
  parsePolicyIdentifier,
} from "@softmax/web-features/policies";
import { getActualViewer } from "@/data/viewer";
import { resolvePolicyIdentity } from "./identity/resolve-policy";

export async function admitPolicyDirectory({
  data: search,
  signal,
}: {
  data: { q?: string | undefined; cursor?: string | null | undefined };
  signal?: AbortSignal | undefined;
}) {
  const viewer = await getActualViewer({ signal });

  if (!viewer || viewer.subject_type === "anonymous") {
    const query = new URLSearchParams();

    if (search.q) query.set("q", search.q);

    if (search.cursor) query.set("cursor", search.cursor);

    throw redirect({
      href: buildHref({
        name: "signIn",
        search: new URLSearchParams({
          redirect_url: buildHref({ name: "policies", search: query }),
        }),
      }),
    });
  }

  return viewer;
}

export async function getPolicy({
  data: { identifier, kind, search },
  signal,
}: {
  data: {
    identifier: string;
    kind: "policy" | "version";
    search: string;
  };
  signal?: AbortSignal | undefined;
}) {
  const parameters = new URLSearchParams();

  if (kind === "version") {
    if (!isPolicyUuid({ value: identifier })) throw notFound();

    parameters.set("policy_version_id", identifier);
  } else if (isPolicyUuid({ value: identifier })) {
    parameters.set("policy_id", identifier);
  } else {
    const parsed = parsePolicyIdentifier({ identifier });
    parameters.set("name_exact", parsed.name);

    if (parsed.version !== null)
      parameters.set("version", String(parsed.version));
  }

  const policy = await resolvePolicyIdentity({
    parameters,
    allowHiddenVersion: kind === "version",
    signal,
  });
  const { selectedVersion, latestVersion } = policy;
  const version =
    selectedVersion.version === latestVersion.version
      ? null
      : selectedVersion.version;
  const canonicalIdentifier =
    version === null
      ? selectedVersion.name
      : `${selectedVersion.name}:v${version}`;

  if (kind === "policy" && identifier !== canonicalIdentifier) {
    throw redirect({
      href: buildHref({
        name: "policy",
        policyName: selectedVersion.name,
        version,
        search: new URLSearchParams(search),
      }),
    });
  }

  return policy;
}
